import React, { useCallback, useEffect, useState } from 'react';
import { Drawer, Table, Alert, Spin, Tag, Tooltip, Typography, Segmented, Space } from 'antd';
import {
  fetchReceivables, fetchPayables, fetchAdvances, fetchStock, fetchCustomerSales,
  fetchWaste, fetchEfficiencyGain,
} from './api';
import { palette, money, qty, prettyDate, prettyMonth } from './theme';
import { Legend } from './charts';
import { filterRows, isBoth } from './scope';

const { Text } = Typography;

// Long names get an ellipsis AND a tooltip. `ellipsis: true` alone gives no
// tooltip once a column has a custom render, so a truncated customer name
// becomes unreadable with no way to see the rest.
// '—' for null/undefined: a bare `dataIndex` column renders a null value as
// nothing at all, and an empty cell reads as "did this load wrong" rather than
// "this row genuinely has no branch". The petty-cash row (no branch, no
// account number) is the case this exists for.
const DASH = '—';
const placeholder = (v) => (v === null || v === undefined || v === '' ? DASH : v);

const nameColumn = (title, dataIndex) => ({
  title,
  dataIndex,
  ellipsis: { showTitle: false },
  render: (v) => <Tooltip title={placeholder(v)}>{placeholder(v)}</Tooltip>,
});

// Dropped entirely when the header's scope has already narrowed the table to
// one company: a column whose every cell reads "RBS", carrying a filter that
// can only ever remove rows, is noise.
const companyColumn = {
  title: 'Company',
  dataIndex: 'company',
  width: 90,
  filters: [{ text: 'RBS', value: 'RBS' }, { text: 'SBY', value: 'SBY' }],
  onFilter: (v, r) => r.company === v,
  render: (v) => <Tag>{v}</Tag>,
};

// Money reads right-aligned with tabular figures so the columns line up; the
// value itself stays in ink, never in a series colour.
const moneyColumn = (title, dataIndex, extra = {}) => ({
  title,
  dataIndex,
  align: 'right',
  width: 140,
  render: (v) => (
    <span style={{ fontVariantNumeric: 'tabular-nums',
                   color: Number(v) < 0 ? palette.critical : palette.ink }}>
      {money(v)}
    </span>
  ),
  sorter: (a, b) => Number(a[dataIndex]) - Number(b[dataIndex]),
  ...extra,
});

const agingColumns = (advancesLabel, scope) => [
  ...(isBoth(scope) ? [companyColumn] : []),
  { title: 'Code', dataIndex: 'partnerCode', width: 90 },
  nameColumn('Name', 'partnerName'),
  moneyColumn('0–30', 'current'),
  moneyColumn('31–60', 'days31to60'),
  moneyColumn('61–90', 'days61to90'),
  moneyColumn('91–120', 'days91to120'),
  moneyColumn('120+', 'daysOver120'),
  moneyColumn('Opening', 'openingBalances'),
  ...(advancesLabel ? [moneyColumn(advancesLabel, 'advances')] : []),
  moneyColumn('Total', 'total', { defaultSortOrder: 'descend', fixed: 'right' }),
];

const agingLegend = [
  ...palette.agingLabels.map((label, i) => ({ label, color: palette.aging[i] })),
  { label: 'Opening balances (not aged)', color: palette.opening },
];

/**
 * Why an "opening balances" column exists on both aging tables.
 *
 * Both systems went live in early April 2026 with every opening balance posted
 * on one day, so the tenant aging report shows all of it as 91–120 days old
 * regardless of the real age. Holding it in its own column keeps the day
 * buckets meaning what they say.
 */
const OPENING_NOTE =
  'Opening balances were all posted at go-live (April 2026) and would otherwise '
  + 'sit in the oldest bucket regardless of their real age, so they are held out '
  + 'of the day buckets and shown separately.';

const VIEWS = {
  cash: {
    title: 'Cash Flows',
    // Already in the overview payload; no second request for eight-odd rows.
    fromOverview: (o) => o.bankAccounts,
    note: 'Balances are derived from posted, non-reversed journal entries. Bank rows '
        + 'are the accounts registered in the tenant Bank master (a GL account listed '
        + 'more than once there still contributes only one row). The Cash in Hand and '
        + 'Petty cash rows per company are resolved from their own account '
        + 'determinations rather than the Bank master, so neither has a branch or '
        + 'account number.',
    columns: (scope) => [
      ...(isBoth(scope) ? [companyColumn] : []),
      { title: 'A/C code', dataIndex: 'accountCode', width: 100 },
      nameColumn('Bank', 'bankName'),
      nameColumn('Branch', 'branchName'),
      {
        title: 'Account number', dataIndex: 'accountNumber', width: 170,
        render: (v) => placeholder(v),
      },
      moneyColumn('Balance', 'balance', { defaultSortOrder: 'descend' }),
    ],
    rowKey: (r) => `${r.company}-${r.accountCode}`,
  },

  receivables: {
    title: 'Customer Outstanding',
    fetch: fetchReceivables,
    note: `Outstanding invoices plus journal-entry receivables. ${OPENING_NOTE} `
        + 'Customer advances are not netted off here — they are on the Advances held tile. '
        + 'The tenant\u2019s own Sales Outstanding Aging report totals NET of those advances, '
        + 'so its total is this figure minus the advances, not this figure. The tile shows both.',
    legend: agingLegend,
    columns: (scope) => agingColumns(null, scope),
    rowKey: (r) => `${r.company}-${r.partnerCode}`,
  },

  payables: {
    title: 'Supplier Outstanding',
    fetch: fetchPayables,
    note: `Outstanding GRNs and AP invoices plus journal-entry payables, net of `
        + `supplier advances already paid. ${OPENING_NOTE}`,
    legend: [...agingLegend, { label: 'Supplier advances (credit)', color: palette.advance }],
    columns: (scope) => agingColumns('Advances', scope),
    rowKey: (r) => `${r.company}-${r.partnerCode}`,
  },

  advances: {
    title: 'Advances held',
    fetch: fetchAdvances,
    note: 'Customer money received against work not yet invoiced, plus unused '
        + 'credit notes. This is a liability: it is work owed, not cash earned.',
    columns: (scope) => [
      ...(isBoth(scope) ? [companyColumn] : []),
      {
        title: 'Type', dataIndex: 'kind', width: 130,
        render: (v) => <Tag color={v === 'ADVANCE' ? 'blue' : 'gold'}>
          {v === 'ADVANCE' ? 'Advance' : 'Credit note'}
        </Tag>,
      },
      { title: 'Code', dataIndex: 'partnerCode', width: 90 },
      nameColumn('Customer', 'partnerName'),
      { title: 'Reference', dataIndex: 'referenceNumber', width: 150 },
      { title: 'Date', dataIndex: 'date', width: 130, render: prettyDate },
      moneyColumn('Amount', 'amount', { defaultSortOrder: 'descend' }),
    ],
    rowKey: (r, i) => `${r.company}-${r.referenceNumber}-${i}`,
  },

  stock: {
    title: 'Stock on hand',
    fetch: fetchStock,
    note: 'Every store, every stock-record item, valued at batch cost. The tenant Stock in '
        + 'Hand report covers the DEFAULT store only. This table does not add up to the '
        + '"Total inventory" tile above — that figure is now sourced from the general ledger '
        + '(so it reconciles to the Trial Balance) and shown per account, not per item, in the '
        + 'inventory breakdown on the dashboard.',
    columns: (scope) => [
      ...(isBoth(scope) ? [companyColumn] : []),
      {
        title: 'Store', dataIndex: 'storeName', width: 130,
        filters: [
          { text: 'DEFAULT', value: 'DEFAULT' },
          { text: 'PRODUCTION', value: 'PRODUCTION' },
          { text: 'DAMAGE', value: 'DAMAGE' },
        ],
        onFilter: (v, r) => r.storeName === v,
      },
      { title: 'Item code', dataIndex: 'itemCode', width: 110 },
      nameColumn('Item', 'itemName'),
      {
        title: 'Quantity', dataIndex: 'quantity', align: 'right', width: 120,
        render: (v) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{qty(v)}</span>,
        sorter: (a, b) => Number(a.quantity) - Number(b.quantity),
      },
      moneyColumn('Value', 'value', { defaultSortOrder: 'descend' }),
    ],
    rowKey: (r) => `${r.company}-${r.storeName}-${r.itemCode}`,
  },

  customers: {
    title: 'Sales by customer',
    fetch: fetchCustomerSales,
    hasMonthPicker: true,
    note: 'Gross invoiced value, matching the tenant customer-wise sales report — '
        + 'that report does not net off sales returns, so neither does this.',
    columns: (scope) => [
      ...(isBoth(scope) ? [companyColumn] : []),
      { title: 'Code', dataIndex: 'customerCode', width: 90 },
      nameColumn('Customer', 'customerName'),
      {
        title: 'Invoices', dataIndex: 'invoiceCount', align: 'right', width: 100,
        sorter: (a, b) => a.invoiceCount - b.invoiceCount,
      },
      {
        title: 'Share', dataIndex: 'shareOfMonth', align: 'right', width: 90,
        render: (v) => <span style={{ fontVariantNumeric: 'tabular-nums',
                                      color: palette.inkSecondary }}>{Number(v).toFixed(1)}%</span>,
      },
      moneyColumn('Amount', 'amount', { defaultSortOrder: 'descend' }),
    ],
    rowKey: (r) => `${r.company}-${r.customerCode}`,
  },

  waste: {
    title: 'Waste',
    fetch: fetchWaste,
    note: 'Wastage raised this month, excluding cancelled wastages. Valued at the FIFO '
        + 'batch cost of the raw material consumed at the moment each wastage was recorded '
        + '— the same figure the tenant Wastage Report sums, so the two cannot disagree.',
    columns: (scope) => [
      ...(isBoth(scope) ? [companyColumn] : []),
      { title: 'WN number', dataIndex: 'wnNumber', width: 140 },
      { title: 'Date', dataIndex: 'wastageDate', width: 110, render: prettyDate },
      { title: 'Item code', dataIndex: 'itemCode', width: 100 },
      nameColumn('Item', 'itemName'),
      {
        title: 'Type', dataIndex: 'wastageType', width: 100,
        render: (v) => <Tag color={v === 'ABNORMAL' ? 'red' : 'default'}>{placeholder(v)}</Tag>,
      },
      {
        title: 'Quantity', dataIndex: 'quantity', align: 'right', width: 110,
        render: (v) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{qty(v)}</span>,
        sorter: (a, b) => Number(a.quantity) - Number(b.quantity),
      },
      moneyColumn('Unit cost', 'unitCost'),
      moneyColumn('Total cost', 'totalCost', { defaultSortOrder: 'descend' }),
    ],
    rowKey: (r, i) => `${r.company}-${r.wnNumber}-${i}`,
  },

  efficiencyGain: {
    title: 'Efficiency gain',
    fetch: fetchEfficiencyGain,
    // The valuation basis has to be spelled out here as plainly as possible:
    // the completion record itself is zero-cost on purpose (no raw material is
    // consumed), so a reader who does not know that would otherwise assume
    // this table is simply wrong.
    note: 'Extra finished-goods output this month from EFFICIENCY_GAIN production '
        + 'completions, excluding cancelled ones. The tenant app records these at zero '
        + 'cost on purpose — no raw material is consumed, and the gain is recognised only '
        + 'when the goods are later sold — so this is not a sum of anything on the '
        + 'completion itself. Each row is instead valued at the item\'s own '
        + 'quantity-weighted average unit cost from its NORMAL completions, i.e. what the '
        + 'same output would normally have cost to produce. An item never produced under '
        + 'NORMAL has no cost basis yet — those rows are flagged "No cost history" below '
        + 'and value at Rs 0 rather than being guessed at.',
    columns: (scope) => [
      ...(isBoth(scope) ? [companyColumn] : []),
      { title: 'PCN number', dataIndex: 'pcnNumber', width: 140 },
      { title: 'Date', dataIndex: 'completionDate', width: 110, render: prettyDate },
      { title: 'Item code', dataIndex: 'itemCode', width: 100 },
      nameColumn('Item', 'itemName'),
      {
        title: 'Quantity', dataIndex: 'quantity', align: 'right', width: 110,
        render: (v) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{qty(v)}</span>,
        sorter: (a, b) => Number(a.quantity) - Number(b.quantity),
      },
      moneyColumn('Unit cost', 'unitCost'),
      moneyColumn('Value', 'value', { defaultSortOrder: 'descend' }),
      {
        // hasCostBasis=false means the item has never been produced under
        // NORMAL, so the Rs 0 beside it is "no cost basis yet", not "this
        // output is worthless" — without this column the two look identical.
        title: 'Cost basis', dataIndex: 'hasCostBasis', width: 150,
        filters: [
          { text: 'Historical cost', value: true },
          { text: 'No cost history', value: false },
        ],
        onFilter: (v, r) => r.hasCostBasis === v,
        render: (v) => (v
          ? <Tag color="default">Historical cost</Tag>
          : <Tooltip title="This item has never been produced under NORMAL, so there is no cost yet to value it against.">
              <Tag color="orange">No cost history</Tag>
            </Tooltip>),
      },
    ],
    rowKey: (r, i) => `${r.company}-${r.pcnNumber}-${i}`,
  },
};

export default function DetailDrawer({ view, overview, months, scope, onClose }) {
  const config = view ? VIEWS[view] : null;
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [month, setMonth] = useState(null);

  // Reset the month picker whenever a different drawer opens, so reopening
  // "Sales by customer" does not silently show a month chosen an hour ago.
  useEffect(() => { setMonth(null); }, [view]);

  const load = useCallback(async () => {
    if (!config) return;
    if (config.fromOverview) {
      setRows(config.fromOverview(overview));
      return;
    }
    // Drop the previous drawer's rows before fetching. Without this the table
    // renders the last drawer's data against this drawer's columns while the
    // request is in flight — every cell blank or NaN, because a receivable has
    // no `customerCode`. It looked exactly like a broken API, and the API was
    // fine. Dashboard also keys this component on `view`, which makes the reset
    // structural rather than something a future edit can quietly drop.
    setRows(null);
    setLoading(true);
    setError(null);
    try {
      setRows(await config.fetch(config.hasMonthPicker ? month : undefined));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [config, overview, month]);

  useEffect(() => { load(); }, [load]);

  if (!config) return <Drawer open={false} />;

  const monthOptions = (months || []).map((p) => ({
    label: prettyMonth(p.month), value: p.month,
  }));
  const selectedMonth = month || (monthOptions.length
    ? monthOptions[monthOptions.length - 1].value : undefined);

  return (
    <Drawer
      open={!!view}
      onClose={onClose}
      title={config.title}
      width="min(1100px, 96vw)"
      destroyOnClose
    >
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Text type="secondary" style={{ fontSize: 13 }}>{config.note}</Text>

        {config.legend && <Legend items={config.legend} />}

        {config.hasMonthPicker && monthOptions.length > 1 && (
          <Segmented
            options={monthOptions}
            value={selectedMonth}
            onChange={setMonth}
          />
        )}

        {error && <Alert type="error" message={error} showIcon />}

        {loading && !rows ? (
          <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>
        ) : (
          <Table
            size="small"
            loading={loading}
            dataSource={filterRows(rows, scope)}
            columns={config.columns(scope)}
            rowKey={config.rowKey}
            pagination={{ pageSize: 25, showSizeChanger: true, size: 'small' }}
            scroll={{ x: 'max-content' }}
          />
        )}
      </Space>
    </Drawer>
  );
}
